import React from "react";

/**
 * QueryPlanCard
 * ──────────────
 * Shows the multi-source query plan before execution.
 * Rendered when structured_data.structured_data_type === "query_plan".
 *
 * Props
 * -----
 * data : query_plan payload from format_query_plan tool
 */
const QueryPlanCard = ({ data }) => {
  if (!data) return null;

  const { title = "Query Plan", steps = [] } = data;

  const platformColors = {
    SNOWFLAKE: "text-blue-600 bg-blue-50",
    SALESFORCE: "text-indigo-600 bg-indigo-50",
    HUBSPOT: "text-orange-600 bg-orange-50",
    POSTGRESQL: "text-teal-600 bg-teal-50",
    MYSQL: "text-teal-600 bg-teal-50",
    AWS_S3: "text-amber-600 bg-amber-50",
    "REST API": "text-purple-600 bg-purple-50",
    "IN-PROCESS": "text-gray-600 bg-gray-100",
  };

  const getColor = (source) =>
    platformColors[(source || "").toUpperCase().trim()] || "text-gray-600 bg-gray-100";

  return (
    <div className="w-full max-w-2xl rounded-2xl border border-gray-100 bg-white shadow-md overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
        <span className="text-[0.85rem] font-bold text-gray-700">{title}</span>
        <span className="text-[0.72rem] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
          {steps.length} {steps.length === 1 ? "step" : "steps"}
        </span>
      </div>

      {/* Steps */}
      <div className="px-4 py-3 space-y-2">
        {steps.map((step, i) => {
          const isLast = i === steps.length - 1;
          return (
            <div key={i} className="flex gap-3">
              {/* Step number + connector */}
              <div className="flex flex-col items-center shrink-0">
                <div className="w-7 h-7 rounded-full border-2 border-blue-200 bg-blue-50 flex items-center justify-center text-[0.72rem] font-extrabold text-blue-600">
                  {step.order || i + 1}
                </div>
                {!isLast && (
                  <div className="w-0.5 flex-1 bg-gray-100 my-1 min-h-[16px]" />
                )}
              </div>

              {/* Step details */}
              <div className="flex-1 pb-1">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <p className="text-[0.88rem] font-semibold text-gray-800 leading-snug">
                    {step.action}
                  </p>
                  {step.source && (
                    <span className={`text-[0.65rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 ${getColor(step.source)}`}>
                      {step.source}
                    </span>
                  )}
                </div>
                {(step.entity || step.note) && (
                  <p className="text-[0.75rem] text-gray-400 font-medium mt-0.5 leading-snug">
                    {step.entity && <span className="font-semibold text-gray-500">{step.entity}</span>}
                    {step.entity && step.note && " · "}
                    {step.note}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QueryPlanCard;
